import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../../services/api';
import './TimeSlotSelector.scss';

const TimeSlotSelector = ({ date, onTimeSlotSelect, selectedTimeSlot, onBack, location, practitioner }) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [groupedSlots, setGroupedSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  useEffect(() => {
    if (date) {
      fetchAvailableSlots();
    }
  }, [date, location, practitioner]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setError('');
    try {
      const locationId = location?._id || location;
      const practitionerId = practitioner?._id || practitioner;
      const response = await bookingAPI.getAvailableSlots(date, locationId, practitionerId);
      
      const slots = response.data.availableSlots || [];
      const grouped = response.data.groupedByPeriod || {};
      
      setAvailableSlots(slots);
      setGroupedSlots(grouped);
      
      // Auto-select first period if available
      if (!selectedPeriod) {
        if (grouped.morning?.length > 0) {
          setSelectedPeriod('morning');
        } else if (grouped.afternoon?.length > 0) {
          setSelectedPeriod('afternoon');
        } else if (grouped.evening?.length > 0) {
          setSelectedPeriod('evening');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load available time slots');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  const getAvailabilityPeriod = (time) => {
    const [hours] = time.split(':');
    const hour = parseInt(hours);
    
    if (hour >= 6 && hour < 12) {
      return 'morning';
    } else if (hour >= 12 && hour < 17) {
      return 'afternoon';
    } else {
      return 'evening';
    }
  };

  const handleSlotClick = (slot) => {
    const period = getAvailabilityPeriod(slot.time);
    onTimeSlotSelect(slot.time, period);
  };

  const handlePeriodTabClick = (period) => {
    setSelectedPeriod(period);
  };

  const getPeriodSlots = () => {
    if (!selectedPeriod || !groupedSlots[selectedPeriod]) {
      return [];
    }
    return groupedSlots[selectedPeriod];
  };

  if (loading) {
    return (
      <div className="time-slot-selector">
        <div className="loading-state">Loading available time slots...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="time-slot-selector">
        <div className="error-state">{error}</div>
        <button className="btn-back" onClick={onBack}>Go Back</button>
      </div>
    );
  }

  const totalSlots = availableSlots.length;
  const periodSlots = getPeriodSlots();

  return (
    <div className="time-slot-selector">
      <div className="header-section">
        <button className="btn-back" onClick={onBack}>
          ← Back
        </button>
        <h2>Select a Time</h2>
        <p className="selected-date">{formatDate(date)}</p>
      </div>

      {totalSlots === 0 ? (
        <div className="no-slots">
          <p>No available time slots for this date.</p>
          <button className="btn-back" onClick={onBack}>Choose Different Date</button>
        </div>
      ) : (
        <>
          <div className="period-tabs">
            {groupedSlots.morning?.length > 0 && (
              <button
                className={`period-tab ${selectedPeriod === 'morning' ? 'active' : ''}`}
                onClick={() => handlePeriodTabClick('morning')}
              >
                <span className="period-indicator morning"></span>
                Morning ({groupedSlots.morning.length})
              </button>
            )}
            {groupedSlots.afternoon?.length > 0 && (
              <button
                className={`period-tab ${selectedPeriod === 'afternoon' ? 'active' : ''}`}
                onClick={() => handlePeriodTabClick('afternoon')}
              >
                <span className="period-indicator afternoon"></span>
                Afternoon ({groupedSlots.afternoon.length})
              </button>
            )}
            {groupedSlots.evening?.length > 0 && (
              <button
                className={`period-tab ${selectedPeriod === 'evening' ? 'active' : ''}`}
                onClick={() => handlePeriodTabClick('evening')}
              >
                <span className="period-indicator evening"></span>
                Evening ({groupedSlots.evening.length})
              </button>
            )}
          </div>

          <p className="subtitle">
            {selectedPeriod ? `${selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} time slots` : 'Available time slots'}
          </p>
          
          <div className="time-slots-grid">
            {periodSlots.length > 0 ? (
              periodSlots.map((slot, index) => (
                <button
                  key={index}
                  className={`time-slot ${selectedTimeSlot === slot.time ? 'selected' : ''}`}
                  onClick={() => handleSlotClick(slot)}
                >
                  {slot.display || formatTime(slot.time)}
                </button>
              ))
            ) : (
              <p className="no-period-slots">No {selectedPeriod} slots available</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TimeSlotSelector;
